/**
 * Body Scroll Lock Hook
 *
 * Prevents body scrolling when a modal or drawer is open.
 * Uses a global reference counter so nested modals work correctly —
 * scroll is only restored when ALL locks are released.
 *
 * @example
 * useBodyScrollLock(isModalOpen)
 */

import { useEffect } from 'react'

// Global counter: scroll is locked when > 0, unlocked when 0
let lockCount = 0
let savedBodyOverflow = ''
let savedDocOverflow = ''

function updateBodyScroll() {
  if (lockCount > 0) {
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = savedBodyOverflow
    document.documentElement.style.overflow = savedDocOverflow
  }
}

/**
 * Lock body scroll when active.
 * Safe for nested usage — multiple components can lock simultaneously.
 *
 * Call this only from components that occlude the viewport when open.
 * For responsive components that are full-viewport on mobile and a
 * fixed side-panel on desktop, gate with `isMobile`:
 * `useBodyScrollLock(isOpen && isMobile)` — see CompanyInfoModal /
 * AiChatPanel for the established pattern (#6798).
 *
 * @param isLocked - Whether this component wants body scroll locked
 */
export function useBodyScrollLock(isLocked: boolean) {
  useEffect(() => {
    if (!isLocked) return

    if (lockCount === 0) {
      savedBodyOverflow = document.body.style.overflow
      savedDocOverflow = document.documentElement.style.overflow
    }
    lockCount++
    updateBodyScroll()

    return () => {
      lockCount--
      updateBodyScroll()
    }
  }, [isLocked])
}
